/**
 * Chunked preview (design M7). The preview route takes at most 100 tenant ids, so
 * the web sends a large selection in chunks, one after another, and sums the
 * answers. The sum is a lower bound: any chunk the server could not fully read
 * makes the whole result `incomplete`.
 */

export const PREVIEW_CHUNK_SIZE = 100;

export interface PreviewResult {
    readonly overwriteCount: number;
    readonly entitlementHeld: number;
    readonly incomplete: boolean;
}

export interface PreviewProgress {
    readonly done: number;
    readonly total: number;
}

export function chunkTenantIds(tenantIds: readonly string[]): string[][] {
    const chunks: string[][] = [];
    for (let start = 0; start < tenantIds.length; start += PREVIEW_CHUNK_SIZE) {
        chunks.push(tenantIds.slice(start, start + PREVIEW_CHUNK_SIZE));
    }
    return chunks;
}

/** Sequential on purpose: each request already fans out server-side. Rejects on the first failure. */
export async function runChunkedPreview(
    tenantIds: readonly string[],
    preview: (chunk: readonly string[]) => Promise<PreviewResult>,
    onProgress?: (progress: PreviewProgress) => void,
): Promise<PreviewResult> {
    const chunks = chunkTenantIds(tenantIds);
    const total = chunks.length;
    let sum: PreviewResult = { overwriteCount: 0, entitlementHeld: 0, incomplete: false };

    onProgress?.({ done: 0, total });
    for (const [index, chunk] of chunks.entries()) {
        const result = await preview(chunk);
        sum = {
            overwriteCount: sum.overwriteCount + result.overwriteCount,
            entitlementHeld: sum.entitlementHeld + result.entitlementHeld,
            incomplete: sum.incomplete || result.incomplete,
        };
        onProgress?.({ done: index + 1, total });
    }
    return sum;
}

export function previewProgressLabel(progress: PreviewProgress): string {
    return `Checking clients… ${String(progress.done)} of ${String(progress.total)}`;
}
